import type { UTCTimestamp } from "lightweight-charts";
import { useEffect, useRef } from "react";
import {
	type ChartingLibraryWidgetOptions,
	type IChartingLibraryWidget,
	type LanguageCode,
	type PineJS,
	type ResolutionString,
	widget,
} from "@/charting_library";
import { callIfExists } from "@/lib/utils/callIfExists/callIfExists";
import { settings } from "@/lib/utils/settings/settings";
import {
	DEFAULT_STATE_VALUES,
	DEFAULT_TICKER,
} from "@/shared/constants/common";
import { EVENTS, globalEventEmitter } from "@/shared/eventEmiters/consts";
import { TIMEFRAME_MAP } from "../../services/PriceService/PriceService";
import { Timeframes } from "../../types";
import type { ChartProps } from "../Chart";
import { CustomDatafeed, getResolutionKeyByValue } from "./datafeed";
import { customIndicatorsGetter } from "./helpers/features/features";
import {
	drawAllReports,
	setDefaultRange,
	setSubscriptions,
	updateState,
} from "./helpers/onChartReady/onChartReady";

export interface ChartContainerProps {
	symbol: ChartingLibraryWidgetOptions["symbol"];
	interval: ChartingLibraryWidgetOptions["interval"];

	// BEWARE: no trailing slash is expected in feed URL
	datafeedUrl: string;
	libraryPath: ChartingLibraryWidgetOptions["library_path"];
	chartsStorageUrl: ChartingLibraryWidgetOptions["charts_storage_url"];
	chartsStorageApiVersion: ChartingLibraryWidgetOptions["charts_storage_api_version"];
	clientId: ChartingLibraryWidgetOptions["client_id"];
	userId: ChartingLibraryWidgetOptions["user_id"];
	fullscreen: ChartingLibraryWidgetOptions["fullscreen"];
	autosize: ChartingLibraryWidgetOptions["autosize"];
	studiesOverrides: ChartingLibraryWidgetOptions["studies_overrides"];
	container: ChartingLibraryWidgetOptions["container"];
}

const getLanguageFromURL = (): LanguageCode | null => {
	// biome-ignore lint/complexity/noUselessEscapeInRegex: correct regexp
	const regex = /[\?&]lang=([^&#]*)/;
	const results = regex.exec(location.search);
	return results === null
		? null
		: (decodeURIComponent(results[1].replace(/\+/g, " ")) as LanguageCode);
};

export const AdvancedChart: React.FC<ChartProps> = ({
	state,
	onStateChange,
}) => {
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const activeChartWidgetRef = useRef<IChartingLibraryWidget | null>(null);
	const shouldProcessReloadRef = useRef<boolean>(true);

	const defaultProps: Partial<ChartContainerProps> = {
		interval: "D" as ResolutionString,
		libraryPath: "/charting_library/",
		fullscreen: false,
		autosize: true,
	};

	const ticker = state?.ticker || DEFAULT_TICKER;
	const interval = getResolutionKeyByValue(
		state?.timeFrame || DEFAULT_STATE_VALUES.timeFrame,
	);

	useEffect(() => {
		if (chartContainerRef.current) {
			chartContainerRef.current.style.pointerEvents = state?.isPanelOpen
				? "none"
				: "auto";
		}
	}, [state?.isPanelOpen]);

	useEffect(() => {
		const handleResizing = (isResizing: boolean) => {
			if (chartContainerRef.current) {
				chartContainerRef.current.style.pointerEvents = isResizing
					? "none"
					: "auto";
			}
		};
		globalEventEmitter.on(EVENTS.RESIZING, handleResizing);

		return () => {
			globalEventEmitter.off(EVENTS.RESIZING, handleResizing);
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't need to run effect on every onStateChange change, as it's created on every mosaic rerender
	useEffect(() => {
		if (activeChartWidgetRef.current && state?.openIndicatorsDialog) {
			activeChartWidgetRef.current.onChartReady(() => {
				activeChartWidgetRef
					.current!.chart()
					.executeActionById("insertIndicator");
				callIfExists(onStateChange)({ openIndicatorsDialog: false });
			});
		}
	}, [state?.openIndicatorsDialog]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't need to run effect on every onStateChange change, as it's created on every mosaic rerender
	useEffect(() => {
		const emReports: Set<UTCTimestamp> = new Set();

		const widgetOptions: ChartingLibraryWidgetOptions = {
			symbol: ticker,
			datafeed: new CustomDatafeed(),
			interval: interval as ChartingLibraryWidgetOptions["interval"],
			container: chartContainerRef.current!,
			library_path: defaultProps.libraryPath as string,

			locale: getLanguageFromURL() || "en",
			disabled_features: [
				"use_localstorage_for_settings",
				"timeframes_toolbar",
				"header_widget",
			],
			enabled_features: ["study_templates", "hide_left_toolbar_by_default"],
			client_id: defaultProps.clientId,
			user_id: defaultProps.userId,
			fullscreen: defaultProps.fullscreen,
			autosize: defaultProps.autosize,
			overrides: {
				"paneProperties.background": "black",
				"paneProperties.backgroundType": "solid",
				"paneProperties.gridLinesMode": "none",
				"mainSeriesProperties.showPriceLine": false,
				color: "#FFFFFF",
			},
			theme: "dark",
			loading_screen: {
				backgroundColor: "black",
			},
			timezone: "exchange",
			debug: false,
			custom_indicators_getter: (PineJS: PineJS) =>
				customIndicatorsGetter(state, PineJS),
		};
		const tvWidget = new widget(widgetOptions);
		activeChartWidgetRef.current = tvWidget;
		shouldProcessReloadRef.current = true;

		const eventSource = new EventSource(
			`${settings.wsHost}/live/${TIMEFRAME_MAP[state.timeFrame ?? Timeframes.ONE_DAY].wsType}/${ticker}`,
		);
		eventSource.addEventListener("reload", () => {
			if (shouldProcessReloadRef.current) {
				shouldProcessReloadRef.current = false;
				tvWidget.resetCache();
				tvWidget.activeChart().resetData();
			}
		});

		tvWidget.onChartReady(() => {
			setDefaultRange(tvWidget, state);
			updateState(tvWidget, state);
			setSubscriptions(tvWidget, state, callIfExists(onStateChange));
			drawAllReports(tvWidget, interval!, ticker, emReports);
		});

		return () => {
			tvWidget.remove();
			eventSource?.close();
		};
	}, [
		ticker,
		interval,
		state?.applyPanels,
		state?.applyDatasets,
		defaultProps.autosize,
		defaultProps.clientId,
		defaultProps.userId,
		defaultProps.libraryPath,
		defaultProps.fullscreen,
	]);

	return <div ref={chartContainerRef} className="h-[calc(100%-20px)]" />;
};
